import React, { useEffect, useId, useRef, useState } from 'react';
import { FiMove, FiChevronUp, FiChevronDown, FiTrash2, FiPlus, FiLink } from 'react-icons/fi';
import { Button } from '../../../components/common';
import { useSortable } from '../../cms/components/useSortable';
import { billingClientService } from '../../../services/billingClientService';
import { useDebounce } from '../../../hooks';
import './InvoiceLineItemsTable.css';

let keySeq = 0;
/** Stable React key for a line that has no server id yet. */
export const lineKey = () => `line-${Date.now()}-${++keySeq}`;

export const newLine = (hsnCode = '998391') => ({
  key: lineKey(),
  projectId: null,
  description: '',
  designCode: '',
  hsnCode,
  price: '',
});

/**
 * Editable invoice lines. Array order is the printed SR No., so reordering
 * just reorders the array. `rowErrors` is `{ [index]: { price: '…' } }`.
 */
const InvoiceLineItemsTable = ({ items, onChange, rowErrors = {}, defaultHsn = '998391', disabled = false }) => {
  const listId = `${useId()}-design-codes`;
  const [codeQuery, setCodeQuery] = useState(null);
  const [codeOptions, setCodeOptions] = useState([]);
  const debouncedCode = useDebounce(codeQuery, 250);
  const requestRef = useRef(0);

  // Design-code suggestions for whichever row is being typed in
  useEffect(() => {
    if (debouncedCode === null) return;
    const requestId = ++requestRef.current;
    billingClientService
      .designCodes(debouncedCode)
      .then((codes) => {
        if (requestId === requestRef.current) setCodeOptions(codes);
      })
      .catch(() => {});
  }, [debouncedCode]);

  const { move, getItemProps, getItemClass } = useSortable(items, onChange, { disabled });

  // Only the handle starts a drag; a fully draggable row would hijack text
  // selection in its inputs.
  const [armedIndex, setArmedIndex] = useState(null);
  const rowDragProps = (index) => {
    const props = getItemProps(index);
    return {
      ...props,
      draggable: armedIndex === index,
      onDragEnd: (e) => {
        setArmedIndex(null);
        props.onDragEnd?.(e);
      },
    };
  };

  const update = (index, field, value) => {
    onChange(items.map((item, i) => (i === index ? { ...item, [field]: value } : item)));
  };

  const remove = (index) => onChange(items.filter((_, i) => i !== index));

  return (
    <div className="ili">
      <datalist id={listId}>
        {codeOptions.map((code) => <option key={code} value={code} />)}
      </datalist>

      <div className="ili-head" aria-hidden="true">
        <span />
        <span>SR</span>
        <span>Description</span>
        <span>Design code</span>
        <span>HSN/SAC</span>
        <span className="num">Price (₹)</span>
        <span />
      </div>

      {items.length === 0 && (
        <div className="ili-empty">No lines yet. Tick a pending project above, or add a line.</div>
      )}

      <ol className="ili-list">
        {items.map((item, index) => {
          const errors = rowErrors[index] || {};
          return (
            <li key={item.key} className={`ili-row ${getItemClass(index)}`} {...rowDragProps(index)}>
              <span
                className="ili-handle"
                title="Drag to reorder"
                onMouseDown={() => !disabled && setArmedIndex(index)}
                onMouseUp={() => setArmedIndex(null)}
              >
                <FiMove />
              </span>
              <span className="ili-sr" aria-label={`Line ${index + 1}`}>{index + 1}</span>

              <div className="ili-cell ili-desc">
                <label className="ili-label" htmlFor={`${item.key}-desc`}>Description</label>
                <textarea
                  id={`${item.key}-desc`}
                  className={`cf-textarea ${errors.description ? 'is-invalid' : ''}`}
                  rows={2}
                  value={item.description}
                  onChange={(e) => update(index, 'description', e.target.value)}
                  placeholder="What is being billed"
                  disabled={disabled}
                />
                {item.projectId && (
                  <span className="ili-linked" title="Linked to a pending project; it is marked invoiced when this invoice is issued">
                    <FiLink /> Project line
                  </span>
                )}
                {errors.description && <span className="cf-error">{errors.description}</span>}
                {errors.billing_project_id && <span className="cf-error">{errors.billing_project_id}</span>}
              </div>

              <div className="ili-cell">
                <label className="ili-label" htmlFor={`${item.key}-code`}>Design code</label>
                <input
                  id={`${item.key}-code`}
                  className="cf-input"
                  list={listId}
                  value={item.designCode}
                  onChange={(e) => {
                    update(index, 'designCode', e.target.value);
                    setCodeQuery(e.target.value);
                  }}
                  onFocus={() => setCodeQuery(item.designCode)}
                  placeholder="20- DD/GM"
                  autoComplete="off"
                  disabled={disabled}
                />
                {errors.design_code && <span className="cf-error">{errors.design_code}</span>}
              </div>

              <div className="ili-cell">
                <label className="ili-label" htmlFor={`${item.key}-hsn`}>HSN/SAC</label>
                <input
                  id={`${item.key}-hsn`}
                  className="cf-input"
                  value={item.hsnCode}
                  onChange={(e) => update(index, 'hsnCode', e.target.value)}
                  placeholder={defaultHsn}
                  disabled={disabled}
                />
                {errors.hsn_code && <span className="cf-error">{errors.hsn_code}</span>}
              </div>

              <div className="ili-cell">
                <label className="ili-label" htmlFor={`${item.key}-price`}>Price (₹)</label>
                <input
                  id={`${item.key}-price`}
                  className={`cf-input ili-price ${errors.price ? 'is-invalid' : ''}`}
                  inputMode="decimal"
                  value={item.price}
                  onChange={(e) => update(index, 'price', e.target.value)}
                  placeholder="0.00"
                  disabled={disabled}
                />
                {errors.price && <span className="cf-error">{errors.price}</span>}
              </div>

              <div className="ili-row-actions">
                <button type="button" className="action-btn" title="Move up" disabled={disabled || index === 0} onClick={() => move(index, index - 1)}>
                  <FiChevronUp />
                </button>
                <button type="button" className="action-btn" title="Move down" disabled={disabled || index === items.length - 1} onClick={() => move(index, index + 1)}>
                  <FiChevronDown />
                </button>
                <button type="button" className="action-btn delete" title="Remove line" disabled={disabled} onClick={() => remove(index)}>
                  <FiTrash2 />
                </button>
              </div>
            </li>
          );
        })}
      </ol>

      <div className="ili-footer">
        <Button variant="outline" size="small" icon={<FiPlus />} onClick={() => onChange([...items, newLine(defaultHsn)])} disabled={disabled}>
          Add line
        </Button>
      </div>
    </div>
  );
};

export default InvoiceLineItemsTable;
